import { gql } from "lib/apolloClient";
import {
  curriculumProgramFragment,
  staffFragment,
  studentsAlertFragment,
} from "modules/CommonFragments";
import { studentInfofragment } from "StudentPortfolio/routes/PortfolioDetailsV2/modules/ProfileDetailsFragments";

export const otherDetailsFragment = {
  otherDetails: gql`
    fragment otherDetailsItem on OtherUserDetails {
      id
      dob
      primaryNationality
      secondaryNationality
      languages
      pronouns
      firstNameLocale
      lastNameLocale
      birthPlaceLocale
      dobLocale
      religionLocale
      primaryNationalityLocale
      seventhAdditionalField
      eighthAdditionalField
    }
  `,
};

export const studentCourseUserMapFragment = {
  userMap: gql`
    fragment userMapItem on CourseUserMapConnection {
      edges {
        node {
          id
          user {
            id
          }
          tags {
            id
            label
            type
          }
        }
      }
    }
  `,
};

export const studentFragments = {
  studentItem: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      pronouns
      preferredName
      prefix
      suffix
      profileImage
      familyInviteCode
      signInCode
      sourceId
      isArchived
      isArchivedV2(filters: $userArchivedFilters)
      isBlocked
      registrationCategory
      gender
      alertTypes {
        ...alertTypeFields
      }
      ...studentAdditionalInfoItem
      curriculumPrograms(filters: $studentCurriculumProgramFilter) {
        ...curriculumProgramBasicDetailsItem
      }
      yearGroup(filters: $userYearGroupMapFilters) {
        id
        name
        grade(filters: $yearGroupGradeFilters) {
          id
          name
        }
        curriculumProgram {
          ...curriculumProgramBasicDetailsItem
        }
      }

      staffRelation(filters: $staffFilter) @include(if: $withStaffRelation) {
        totalCount
        edges {
          node {
            ...staffItem
            ... on Staff {
              isArchivedV2(filters: $userArchivedFilters)
            }
          }
        }
      }
      families(filters: $familiesFilter) {
        id
        status
        familyMember {
          id
          lastName
          middleName
          firstName
          preferredName
          prefix
          suffix
          pronouns
          email
          type
          profileImage
          isCreatedByOrg
          lastActiveAt
        }
        createdAt
        lastUpdatedAt
      }
      email
      courseMap(filters: $userCourseMapFilters) {
        course {
          id
          title
          profileImageData {
            url
            icon
          }
          isArchived
          curriculumProgram {
            ...curriculumProgramBasicDetailsItem
          }
          genericTags {
            id
            label
            type
          }
          grades(filters: $courseGradeFilters) {
            id
            name
          }
          grade {
            id
            yearGroup {
              id
            }
          }
        }
        tags {
          id
          label
        }
      }
      academicYears {
        id
        startDate
        endDate
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      otherDetails {
        ...otherDetailsItem
      }
      projects {
        id
      }
      studentProjectPortfolio {
        id
      }
      projectGroups {
        id
      }
      enrollmentDate
      withdrawalDate
      type
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${staffFragment.basicStaffDetails}
    ${otherDetailsFragment.otherDetails}
    ${studentInfofragment.additionalInfo}
    ${studentsAlertFragment}
  `,
  studentItemV2: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      pronouns
      preferredName
      prefix
      suffix
      profileImage
      familyInviteCode
      signInCode
      sourceId
      isArchived
      isArchivedV2(filters: $userArchivedFilters)
      isBlocked
      registrationCategory
      gender
      alertTypes {
        ...alertTypeFields
      }
      ...studentAdditionalInfoItem
      curriculumPrograms(filters: $studentCurriculumProgramFilter) {
        ...curriculumProgramBasicDetailsItem
      }
      yearGroup(filters: $userYearGroupMapFilters) {
        id
        name
        grade(filters: $yearGroupGradeFilters) {
          id
          name
        }
        curriculumProgram {
          ...curriculumProgramBasicDetailsItem
        }
      }
      staffRelation(filters: $staffFilter) @include(if: $withStaffRelation) {
        totalCount
        edges {
          node {
            ...staffItem
            ... on Staff {
              isArchivedV2(filters: $userArchivedFilters)
            }
          }
        }
      }
      families(filters: $familiesFilter) {
        id
        status
        familyMember {
          id
          lastName
          middleName
          firstName
          email
          type
          profileImage
        }
        createdAt
        lastUpdatedAt
      }
      email
      coursesV2(
        filters: { archivalState: ALL, academicYears: $academicYears }
      ) {
        totalCount
        archivedCount
      }
      academicYears {
        id
        startDate
        endDate
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      otherDetails {
        ...otherDetailsItem
      }
      projects {
        id
      }
      studentProjectPortfolio {
        id
      }
      projectGroups {
        id
      }
      type
      enrollmentDate
      withdrawalDate
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${staffFragment.basicStaffDetails}
    ${otherDetailsFragment.otherDetails}
    ${studentInfofragment.additionalInfo}
    ${studentsAlertFragment}
  `,
  studentDetails: gql`
    fragment studentItem on Student {
      id
      firstName
      lastName
      profileImage
      email
      sourceId
      registrationCategory
      gender
      yearGroup {
        id
        name
      }
      courseMap {
        course {
          id
          grade {
            id
            yearGroup {
              id
            }
          }
        }
      }
      projects {
        id
      }
      studentProjectPortfolio {
        id
      }
      curriculumPrograms {
        ...curriculumProgramBasicDetailsItem
      }
      otherDetails {
        ...otherDetailsItem
      }
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${otherDetailsFragment.otherDetails}
  `,
  studentFamilyDetails: gql`
    fragment studentItem on Student {
      id
      firstName
      lastName
      profileImage
      familyInviteCode
      familyMembers {
        id
        lastName
        firstName
        profileImage
        email
      }
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
  `,
  studentFamilyMembers: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      profileImage
      email
      sourceId
      families(filters: $familiesFilter) {
        id
        status
        familyMember {
          id
          lastName
          middleName
          firstName
          email
          type
          isCreatedByOrg
        }
        createdAt
        lastUpdatedAt
      }
    }
  `,
};

export const organizationStudentFeedFragment = {
  staffUnitPlanFeedCount: gql`
    fragment staffUnitPlanFeedCountItem on Staff {
      id
      unitPlans(filters: $unitPlanFilters) {
        totalCount
      }
    }
  `,
  organizationStudentFeedEdge: gql`
    fragment organizationStudentFeedEdgeItem on Organization {
      id
      familyMembers(filters: $familyMembersCountFilter) {
        totalCount
      }
      students(
        first: 20
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        blockedCount
        edges {
          node {
            ...studentItem
            allPosts(filters: $postFilters) {
              totalCount
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCountsByFamilyStatus(filters: $familyStatus) {
          totalCount
          totalUnconnected
          totalConnected
          totalInvited
          totalApproveRequested
          totalPending
          totalNotActiveSince
        }
      }
    }
    ${studentFragments.studentItem}
  `,
  organizationStudentFeedEdgeV2: gql`
    fragment organizationStudentFeedEdgeItem on Organization {
      id
      familyMembers(filters: $familyMembersCountFilter) {
        totalCount
      }
      students(
        first: 20
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        allCount
        blockedCount
        archivedCount
        edges {
          node {
            ...studentItem
            allPosts(filters: $postFilters) {
              totalCount
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCountsByFamilyStatus(filters: $familyStatus) {
          totalCount
          totalUnconnected
          totalConnected
          totalInvited
          totalApproveRequested
          totalPending
          totalNotActiveSince
        }
      }
      totalStudentsCountWithoutSearch: students(
        filters: $filtersWithoutSearch
      ) {
        totalCount
      }
    }
    ${studentFragments.studentItemV2}
  `,
};

export const yearGroupFragments = {
  basicYearGroupDetails: gql`
    fragment basicYearGroupDetails on YearGroup {
      id
      name
      curriculumProgram {
        id
        type
        acronym
      }
      grade(filters: $yearGroupGradeFilters) {
        id
        name
        displaySequence
        courses {
          totalCount
        }
      }
      students {
        totalCount
      }
      examSession {
        id
        session
        year
      }
      selectedTranscriptTerms {
        id
      }
      isArchived
      archivedAt
    }
  `,
  portfolioDownloadYearGroupDetails: gql`
    fragment basicYearGroupDetails on YearGroup {
      id
      name
      curriculumProgram {
        id
        type
        acronym
      }
    }
  `,
};

export const additionalDetailsUserFragment = {
  user: gql`
    fragment userItem on User {
      id
      firstName
      middleName
      lastName
      email
      type
      profileImage
      ... on Student {
        sourceId
        isArchivedV2
        curriculumPrograms(filters: { academicYearId: $academicYearId }) {
          id
          type
        }
        academicYears {
          id
          endDate
          startDate
          isCurrentAcademicYear
          isPreviousAcademicYear
          isUpcomingAcademicYear
        }
      }
    }
  `,
};
